import React, { ReactElement, useEffect, useRef, useState } from 'react';
// COMPONENTS
import {
  DrillHoleObject,
  ImportedLog,
  JobsResponse,
  ProjectAllReport,
  ProjectAllReportList,
  ProjectModel,
  ProjectObject,
} from './TypeScript/interfaces';
// MUI
import Backdrop from '@mui/material/Backdrop/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// PACKAGES
import queryString from 'query-string';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AppScopeLoadingComponent from './utils/components/AppScopeLoading';
import { errorHandler } from './utils/error/error-handler';
import {
  getAllReports,
  getProject,
} from './utils/authenticated-requests/projects';
import { useTranslation } from 'react-i18next';
import { GeologContext } from './Contexts/GeologContext';
import {
  DrillholeContext,
  DrillholesMap,
  emptyDrillholesMap,
} from './Contexts/DrillholeContext';
import { queryClient } from './Components/Root/queryClient';
import { appContext } from './Contexts/AppContext';
import AppRouter from './Router/router';
import { getDrillHole } from './utils/authenticated-requests/drillholes';
import {
  createDatabase,
  LitholensLocalDatabaseType,
} from './utils/db/database';
import { ImageDownloadProvider } from './Contexts/ImageDownloadContext';
import { useAppAlert } from './Contexts/AppAlertContext';

interface AppProps {
  user: {
    username: string;
    signInUserSession: {
      idToken: {
        payload: any;
      };
      getIdToken: () => {
        getJwtToken: () => string;
      };
    };
    attributes?: any;
    userDataKey?: string;
  };
  signOut: () => void;
}

const App = ({ user, signOut }: AppProps): ReactElement => {
  const { t } = useTranslation();
  // High level state to control the Side Drawer via sibling Navbar
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [appLoading, setAppLoading] = useState<boolean>(false);
  const [appLoadingMessage, setAppLoadingMessage] = useState<string>('');
  const [appLoadingProgress, setAppLoadingProgress] = useState<number>(-1);
  const [company, setCompany] = useState<string>(
    user.signInUserSession.idToken.payload['custom:company'],
  ); //default company
  const [activeProject, setActiveProject] = useState<ProjectObject>({});
  const [activeJob, setActiveJob] = useState<JobsResponse>({
    company: '',
    description: '',
    id: '',
    project: '',
  });
  const [drillholesMap, setDrillholesMap] =
    useState<DrillholesMap>(emptyDrillholesMap);
  const [drillholesMapReady, setDrillholesMapReady] = useState<boolean>(false);
  const [projectReports, setProjectReports] = useState<ProjectAllReport[]>([]);
  const [projectModels, setProjectModels] = useState<ProjectModel[]>([]);
  const [projectModelsLoading, setProjectModelsLoading] =
    useState<boolean>(true);
  const [logsMetadata, setLogsMetadata] = useState<ImportedLog[]>([]);
  const projectIdRef = useRef<string>();
  const allDrillholesWorkerRef = useRef<Worker>();
  const drillholesMapRef = useRef<DrillholesMap>(emptyDrillholesMap);
  const geologsRef = useRef<
    Record<string, Record<string | number, Record<string, any>[]>>
  >({});
  const { i18n } = useTranslation();
  const { addAppAlert } = useAppAlert();

  const [localDb, setLocalDb] = useState<LitholensLocalDatabaseType>();
  useEffect(() => {
    if (!company || !activeProject.id) {
      return;
    }

    const dbInstance = createDatabase(company, activeProject.id);
    setLocalDb(dbInstance);

    return () => {
      dbInstance.close();
    };
  }, [company, activeProject.id]);

  /**
   * When app loads, start worker
   */
  useEffect(() => {
    initAllDrillholesWorker();
  }, []);

  /**
   * When active project changes, update drillhole context
   */
  useEffect(() => {
    const currentProjectId: string | undefined = activeProject?.id;
    const previousProjectId: string = `${projectIdRef.current}`;
    // maintain ref to project id to compare changes
    projectIdRef.current = currentProjectId;
    //handle differences:
    if (previousProjectId && !currentProjectId) {
      // left project scope > clear drillholes
      resetAllDrillholesWorker();
      resetDrillholesMap();
      resetGeologsContext();
      setProjectReports([]);
      setProjectModels([]);
    } else if (currentProjectId && currentProjectId !== previousProjectId) {
      // entered into or swapped project scope > replace drillholes
      resetAllDrillholesWorker();
      resetDrillholesMap();
      fetchAllDrillholes();
      resetGeologsContext();
      fetchReportsAndModels(currentProjectId);
    }
    // else currentProjectId === previousProjectId => no action
  }, [activeProject]);

  /**
   * get/set app language at app init (changes handled in UserMenu component)
   */
  useEffect(() => {
    const supportsLocalStorage: boolean = window.hasOwnProperty('localStorage');
    const selectedLanguage: string =
      window.localStorage?.getItem(
        `${user.userDataKey}.selectedLanguage.litholens`,
      ) || '';
    const defaultLanguage: string = i18n.options.lng || 'en';
    if (supportsLocalStorage && !selectedLanguage) {
      window.localStorage.setItem(
        `${user.userDataKey}.selectedLanguage.litholens`,
        defaultLanguage,
      );
    } else if (user && supportsLocalStorage && selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [user]);

  const fetchReportsAndModels = (projectId: string) => {
    setAppLoading(true);
    setProjectModelsLoading(true);
    getAllReports({
      company: company,
      projectId,
    })
      .then((data: ProjectAllReportList) => {
        if (data?.items) {
          setProjectReports(data.items);
        } else {
          setProjectReports([]);
        }
        if (data?.models) {
          setProjectModels(data.models);
        } else {
          setProjectModels([]);
        }
      })
      .catch((error) => {
        setProjectReports([]);
        setProjectModels([]);
        errorHandler(error);
      })
      .finally(() => {
        setProjectModelsLoading(false);
        setAppLoading(false);
      });
  };

  /**
   * Terminate the drillholes worker and re-initialize
   *
   * stops fetching drillholes if fetch is in progress
   */
  const resetAllDrillholesWorker = (): void => {
    if (allDrillholesWorkerRef.current) {
      allDrillholesWorkerRef.current.terminate();
      allDrillholesWorkerRef.current = undefined;
      initAllDrillholesWorker();
    }
  };

  /**
   * Initialize drillhole worker
   *
   * instantiates worker and sets event handlers
   */
  const initAllDrillholesWorker = (): void => {
    if (!allDrillholesWorkerRef.current) {
      allDrillholesWorkerRef.current = new Worker(
        '/get-all-drillholes-by-project.js',
      );
    }
    if (allDrillholesWorkerRef.current) {
      const queryString: string = JSON.stringify({
        expand: true,
      });
      allDrillholesWorkerRef.current.onmessage = (
        message: MessageEvent<{ results: DrillHoleObject[]; last: boolean }>,
      ) => {
        setAppLoading(true);
        const { results, last } = message.data;
        populateDrillholeMap(queryString, results);
        if (last) {
          setAppLoading(false);
        }
      };
      allDrillholesWorkerRef.current.onerror = (e: ErrorEvent) => {
        errorHandler(e.error);
        populateDrillholeMap(queryString);
        setAppLoading(false);
      };
    }
  };

  /**
   * Resets drillholes map and fetches drillholes from api
   */
  const hardReloadDrillholesMap = (): void => {
    resetDrillholesMap();
    fetchAllDrillholes();
    queryClient.invalidateQueries({ queryKey: ['drillholeImages'] });
  };

  const hardReloadProjectReportsAndModels = (): void => {
    if (activeProject.id) {
      fetchReportsAndModels(activeProject.id);
    }
  };

  /**
   * Fetch all drillholes
   *
   * starts fetch in web worker
   */
  const fetchAllDrillholes = (): void => {
    if (activeProject && activeProject.id && allDrillholesWorkerRef.current) {
      setDrillholesMapReady(false);

      let apiHostname: string;

      switch (process.env.REACT_APP_ENVIRONMENT) {
        case 'prod':
          apiHostname = 'https://projects.api.goldspot.ca';
          break;
        case 'test':
          apiHostname = 'https://projects.test.api.goldspot.ca';
          break;
        case 'test2':
          apiHostname = 'https://projects.api.test2.als.app';
          break;
        default:
          apiHostname = 'https://projects.test.api.goldspot.ca';
          break;
      }
      const bearerToken: string = user.signInUserSession
        .getIdToken()
        .getJwtToken();
      allDrillholesWorkerRef.current.postMessage({
        apiHostname,
        company,
        project: activeProject.id,
        bearerToken,
      });
    } else {
      const err: Error = new Error('no active project');
      errorHandler(err);
      setDrillholesMapReady(true);
    }
  };

  /**
   * clear all logs from geologs context
   *
   * used when switching projects
   */
  const resetGeologsContext = (): void => {
    geologsRef.current = {};
    setLogsMetadata([]);
  };

  /**
   * Reset drillholes map to empty
   *
   * used for when active project removed from context
   */
  const resetDrillholesMap = (): void => {
    const emptyDrillholesMap: DrillholesMap = {
      byId: {},
      byName: {},
      byQuery: {},
    };
    drillholesMapRef.current = { ...emptyDrillholesMap };
    setDrillholesMap({ ...emptyDrillholesMap });
  };

  const addDrillholeToRefMap = (drillhole: DrillHoleObject): void => {
    drillholesMapRef.current.byId[drillhole.id] = drillhole;
    drillholesMapRef.current.byName[drillhole.name] = drillhole;
  };

  // When updating one drillhole, this removes the old instance first
  const removeDrillholeFromRefMap = (drillholeId: string | number): void => {
    const drillhole: DrillHoleObject =
      drillholesMapRef.current.byId[drillholeId];
    if (drillhole) {
      delete drillholesMapRef.current.byId[drillhole.id];
      delete drillholesMapRef.current.byName[drillhole.name];
    }
  };

  /**
   * Put drillholes objects into maps and add to context
   * @param queryString json.stringified version of the following object:
   * ```
   * const queryString = {
   *   limit: number,
   *   expand: boolean,
   * };
   * ```
   * TODO: expand object with orderby, sortby, etc if it becomes available
   * @param drillholes array of drillhole objects
   */
  const populateDrillholeMap = (
    queryString: string,
    drillholes: DrillHoleObject[] = [],
  ): void => {
    if (!drillholesMapRef.current.byQuery[queryString]) {
      drillholesMapRef.current.byQuery[queryString] = [];
    }
    drillholes.forEach((drillhole: DrillHoleObject) => {
      // if drillhole not yet saved or saved drillhole is not expanded
      if (
        !drillholesMapRef.current.byId.hasOwnProperty(drillhole.id) ||
        !drillholesMapRef.current.byId[drillhole.id].hasOwnProperty('length')
      ) {
        addDrillholeToRefMap(drillhole);
      }
      drillholesMapRef.current.byQuery[queryString].push(
        drillholesMapRef.current.byId[drillhole.id],
      );
    });
    setDrillholesMap({ ...drillholesMapRef.current });
  };

  /**
   * Add imported logs to app
   *
   * (GeologsContext)
   */
  const addLogs = (
    logs: Record<string, Record<string | number, Record<string, any>[]>>,
  ): void => {
    Object.assign(geologsRef.current, logs);
  };

  /**
   * Get imported logs from app
   *
   * (GeologsContext)
   */
  const getLogs = (
    logName: string,
    drillholeId: string | number,
  ): Record<string, any>[] => {
    return geologsRef.current[logName]?.[drillholeId] || [];
  };

  /**
   * Add imported log objects to list in app
   *
   * (GeologsContext)
   */
  const addLogsMetadata = (newLogsMetadata: ImportedLog[]): void => {
    const updatedLogsMetadata: ImportedLog[] = [...logsMetadata];
    newLogsMetadata.forEach((importedLog: ImportedLog) => {
      // remove duplicates before appending new items
      if (logsMetadata.length > 0) {
        // iterate in reverse so that splice doesn't corrupt loop
        logsLoop: for (let i = logsMetadata.length - 1; i >= 0; i--) {
          const existingLog: ImportedLog = logsMetadata[i];
          const metadataItem: (keyof ImportedLog)[] = [
            'fileName',
            'columnHeader',
          ];
          let different: boolean = false;
          propertiesLoop: for (let j = 0; j < metadataItem.length; j++) {
            const property: keyof ImportedLog = metadataItem[j];
            if (importedLog[property] != existingLog[property]) {
              different = true;
              break propertiesLoop;
            }
          }
          if (!different) {
            updatedLogsMetadata.splice(i, 1);
            // if duplicate is found, exit loop
            break logsLoop;
          }
        }
      }
      updatedLogsMetadata.push(importedLog);
    });
    setLogsMetadata(updatedLogsMetadata);
  };

  /**
   * Get project object and update context
   */
  const refreshProjectObject = (projectId?: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const project = projectId || activeProject?.id;
      resetDrillholesMap();
      if (project) {
        getProject(company, project as string, true)
          .then((value: ProjectObject) => {
            setActiveProject(value);
            resolve();
          })
          .catch((err) => {
            errorHandler(err);
            reject(err);
          });
      } else {
        resolve();
      }
    });
  };

  /*
   * Checks if drillhole map is complete based on drillholes count in active project
   * @param project active project
   * @param drillholesMap drillholes map
   * @returns true if drillholes map is complete
   */
  const isDrillholesMapReady = (
    project: ProjectObject,
    drillholesMap: any,
  ): boolean => {
    const numberOfDrillholes: number | undefined =
      project.litholensData?.drillholesCount === null
        ? 0
        : project.litholensData?.drillholesCount;
    const drillholesReady: number = (Object.keys(drillholesMap.byId) || [])
      .length;
    return drillholesReady === numberOfDrillholes;
  };

  const reloadOneDrillhole = async (drillholeId: number): Promise<void> => {
    try {
      setAppLoading(true);
      const updatedDrillhole: DrillHoleObject = await getDrillHole(
        company,
        activeProject.id as string,
        `${drillholeId}`,
      );
      if (updatedDrillhole) {
        removeDrillholeFromRefMap(drillholeId);
        addDrillholeToRefMap(updatedDrillhole);
        setDrillholesMap({ ...drillholesMapRef.current });
      }
    } catch (err: any) {
      addAppAlert({
        severity: 'warning',
        message: t('ALERT_WARNING_REFRESH_ONEDRILLHOLE'),
        details: err.error?.response?.data || undefined,
      });
    } finally {
      setAppLoading(false);
      return;
    }
  };

  useEffect(() => {
    if (
      activeProject.litholensData?.drillholesCount &&
      activeProject.litholensData.drillholesCount > 0
    ) {
      fetchAllDrillholes();
    }
  }, [activeProject]);

  // maintain `drillholesMapReady`
  useEffect(() => {
    const isReady: boolean = isDrillholesMapReady(activeProject, drillholesMap);
    setDrillholesMapReady(isReady);
  }, [activeProject, drillholesMap]);

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        searchStringToObject: (searchString) =>
          queryString.parse(searchString, {
            arrayFormat: 'comma',
          }),
        objectToSearchString: (encodedParams) =>
          queryString.stringify(encodedParams, {
            arrayFormat: 'comma',
            encode: false,
          }),
      }}
    >
      <QueryClientProvider client={queryClient}>
        <appContext.Provider
          value={{
            user,
            appLoading,
            setAppLoading,
            setAppLoadingMessage,
            setAppLoadingProgress,
            company,
            setCompany,
            activeProject,
            setActiveProject,
            refreshProjectObject,
            activeJob,
            setActiveJob,
            db: localDb,
          }}
        >
          <ImageDownloadProvider>
            <DrillholeContext.Provider
              value={{
                drillholesMap,
                drillholesMapReady,
                hardReloadDrillholesMap,
                reloadOneDrillhole,
                projectReports,
                setProjectReports,
                projectModels,
                projectModelsLoading,
                hardReloadProjectReportsAndModels,
              }}
            >
              <GeologContext.Provider
                value={{
                  addLogs,
                  getLogs,
                  logsMetadata,
                  addLogsMetadata,
                }}
              >
                <Box sx={{ display: 'flex' }} className="bg-primary">
                  <React.Suspense fallback={<AppScopeLoadingComponent />}>
                    <AppRouter
                      isDrawerOpen={isDrawerOpen}
                      setIsDrawerOpen={setIsDrawerOpen}
                      signOut={signOut}
                    />
                  </React.Suspense>
                </Box>
                <Backdrop
                  sx={{ color: '#fff', zIndex: '2000' }}
                  open={appLoading}
                >
                  <CircularProgress color="inherit" />
                  <br />
                  <p
                    style={{
                      display: 'block',
                      position: 'fixed',
                      top: 'calc(50% + 16px)',
                      textAlign: 'center',
                    }}
                  >
                    {!!appLoadingMessage && appLoadingMessage}
                    <br />
                    {appLoadingProgress !== -1 && `${appLoadingProgress}%`}
                  </p>
                </Backdrop>
              </GeologContext.Provider>
            </DrillholeContext.Provider>
          </ImageDownloadProvider>
        </appContext.Provider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </QueryParamProvider>
  );
};

export default App;
